import type * as A from '@/api';
import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';

const ns = 'app.services.organizationAIContext';

type Snapshot = A.OrganizationAIContext;

class OrganizationAIContextService {
  static async getByOrgId(orgId: string): Promise<Snapshot | null> {
    const result = await graphql('getOrganizationAIContext', { id: orgId });
    return (result ?? null) as Snapshot | null;
  }

  static async upsert(input: A.UpdateOrganizationAIContextInput): Promise<Snapshot> {
    const orgId = input.id;
    const existing = await this.getByOrgId(orgId);
    if (existing) {
      return (await graphql('updateOrganizationAIContext', { input })) as Snapshot;
    }
    return (await graphql('createOrganizationAIContext', {
      input: { ...input, orgId } as A.CreateOrganizationAIContextInput,
    })) as Snapshot;
  }

  static async getAssetUploadUrl(
    args: A.GetOrganizationAIContextAssetUploadUrlInput,
  ): Promise<A.GetOrganizationAIContextAssetUploadUrlOutput> {
    const out = await graphql('getOrganizationAIContextAssetUploadUrl', { query: args });
    return out as A.GetOrganizationAIContextAssetUploadUrlOutput;
  }

  static async getAssetSignedUrl(
    args: A.GetOrganizationAIContextAssetUrlInput,
  ): Promise<string> {
    const out = (await graphql('getOrganizationAIContextAssetUrl', {
      query: args,
    })) as A.GetOrganizationAIContextAssetUrlOutput;
    return out.url;
  }

  static async putAssetToS3(uploadUrl: string, file: File): Promise<void> {
    // Content-Disposition mirrors the signing lambda — the presigned URL
    // includes content-disposition in SignedHeaders so a missing header here
    // makes S3 reject the PUT with 403 SignatureDoesNotMatch.
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'Content-Disposition': 'attachment',
      },
      body: file,
    });
    if (!res.ok) {
      logger.error({ label: `${ns}.putAssetToS3`, message: `${res.status} ${res.statusText}` });
      throw new Error(`Asset upload failed: ${res.status}`);
    }
  }

  static async sha256Hex(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}

export default OrganizationAIContextService;
